/**
 * features/property/components/CompetingProjectsNearby.jsx (v2.71.65)
 * -------------------------------------------------------------------
 * Builder-only replacement for `<SimilarProperties>` on property detail
 * pages. Per spec STEP 9:
 *
 *   BUILDERS:
 *     - limited "Competing Projects Nearby"
 *     - maximum 5 items
 *     - collapsed section
 *     - no infinite discovery scrolling
 *
 * Architecture:
 *   - Click to expand (collapsed by default — no auto-fetch).
 *   - Fetches `/api/projects?status=approved` once on expand and shows
 *     up to 5 projects matching the same `main_area`.
 *   - Backend strip_project_for_role already hides exact coords + unit
 *     availability for builders viewing competitor projects — this
 *     component just renders the limited data safely.
 *   - Excludes the builder's own projects.
 *
 * Rationale: prevent endless browsing of the buyer-facing project
 * catalog from the property detail page. Workflow-focused.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, ChevronRight, Loader2, Lock, MapPin, Building2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import api from "../../../lib/api";

const MAX_ITEMS = 5;

function priceRangeLabel(units) {
  if (!Array.isArray(units) || units.length === 0) return "—";
  const mins = units.map((u) => u.price_min || 0).filter(Boolean);
  const maxs = units.map((u) => u.price_max || 0).filter(Boolean);
  const lo = mins.length ? Math.min(...mins) : 0;
  const hi = maxs.length ? Math.max(...maxs) : 0;
  if (!lo && !hi) return "—";
  const fmt = (n) => {
    if (!n) return "—";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1).replace(/\.0$/, "")} Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.0$/, "")} L`;
    return `₹${n.toLocaleString("en-IN")}`;
  };
  if (lo && hi && lo !== hi) return `${fmt(lo)} – ${fmt(hi)}`;
  return fmt(lo || hi);
}

export default function CompetingProjectsNearby({ property }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/projects", { params: { status: "approved" } });
      const all = Array.isArray(r.data) ? r.data : [];
      // Filter: same main_area, exclude own projects (server sets is_own=true),
      // cap at MAX_ITEMS
      const mainArea = property?.main_area || property?.area || "";
      const filtered = all
        .filter((p) => !p.is_own)
        .filter((p) => {
          if (!mainArea) return true;
          const pma = (p.main_area || p.location || "").toLowerCase();
          return pma.includes(mainArea.toLowerCase()) || mainArea.toLowerCase().includes(pma);
        })
        .slice(0, MAX_ITEMS);
      setItems(filtered);
    } catch (e) {
      setErr("Failed to load competing projects.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const onOpen = () => {
    setOpen(true);
    if (items === null) fetchItems();
  };

  if (!open) {
    return (
      <div
        data-testid="competing-projects-collapsed"
        className="mt-8 bg-white border border-slate-200 rounded-2xl px-5 py-4 flex items-center gap-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
      >
        <div className="shrink-0 w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center">
          <Boxes size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-slate-900">Competing Projects Nearby</div>
          <div className="text-[11.5px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
            <Lock size={10} /> Limited intel · max {MAX_ITEMS} projects · for competitive awareness only
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onOpen}
          data-testid="competing-projects-load"
          className="h-9 rounded-full text-[12.5px] font-semibold"
        >
          Load nearby <ChevronRight size={13} className="ml-0.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="competing-projects-loaded">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold text-[15px] tracking-tight inline-flex items-center gap-2">
          <Boxes size={14} className="text-indigo-700" /> Competing Projects Nearby
        </h3>
        <Link
          to="/builder/marketplace"
          className="text-[12px] font-semibold text-[#0055FF] hover:underline inline-flex items-center gap-1"
          data-testid="competing-projects-viewall"
        >
          View full marketplace <ChevronRight size={11} />
        </Link>
      </div>
      {loading && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center text-[12.5px] text-slate-500 inline-flex items-center gap-2 justify-center w-full">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}
      {!loading && err && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-[12.5px] text-rose-700">{err}</div>
      )}
      {!loading && !err && items && items.length === 0 && (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-6 text-center text-[12.5px] text-slate-500" data-testid="competing-projects-empty">
          No competing projects in this area yet.
        </div>
      )}
      {!loading && !err && items && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((p) => (
            <Link
              key={p.id}
              to={`/project/${p.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-slate-200 hover:border-[#0055FF]/40 rounded-2xl overflow-hidden group transition-all hover:-translate-y-0.5"
              data-testid={`competing-project-${p.id}`}
            >
              <div className="aspect-[16/10] bg-slate-100 relative overflow-hidden">
                {p.images?.[0] ? (
                  <img
                    src={p.images[0]}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                    <Building2 size={22} />
                  </div>
                )}
                <div className="absolute top-2 right-2 bg-slate-900/80 backdrop-blur-sm text-white text-[9px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1">
                  <Lock size={8} /> Limited
                </div>
              </div>
              <div className="p-3">
                <h4 className="font-heading font-semibold text-[13px] tracking-tight line-clamp-1 text-slate-900 group-hover:text-[#0055FF]">
                  {p.name || "Untitled project"}
                </h4>
                <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                  <MapPin size={9} /> {p.main_area || p.location || "—"}
                </div>
                <div className="text-[12px] font-bold text-slate-900 font-mono-data mt-1.5">
                  {priceRangeLabel(p.units)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      <div className="mt-3 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
        <Lock size={10} /> Showing up to {MAX_ITEMS} nearby projects. Exact coordinates, unit availability &amp; owner details are hidden by policy.
      </div>
    </div>
  );
}
